import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { VoucherDTO } from "~/lib/api";

export interface CustomReferentData {
  name: string;
  email: string;
  surname: string;
  phone: string;
  zipCode?: string;
  // Data di nascita del referente (YYYY-MM-DD). Proviene dal profilo (signup):
  // viene inviata come Code DATEOFBIRTH SOLO nel ReferentData della prenotazione.
  dateOfBirth?: string;
  privacyAgreed?: boolean;
  emailCommunicationAgreed?: boolean;
  profilingAgreed?: boolean;
  dataSharingAgreed?: boolean;
}

export interface CustomGuestData {
  name: string;
  email?: string;
  surname: string;
  isFemale?: boolean;
  zipCode?: string;
}

export interface GuestData {
  Code: "NAME" | "SURNAME" | "EMAILADDRESS" | "GENDER" | "ADDRESS_ZIPCODE";
  Content: string;
}

export interface ReferentData {
  Code:
    | "NAME"
    | "SURNAME"
    | "EMAILADDRESS"
    | "MOBILENUMBER"
    | "DATEOFBIRTH"
    | "PRIVACYAGREED"
    | "EMAILCOMMUNICATIONAGREED"
    | "PROFILINGAGREED"
    | "DATASHARINGAGREED"
    | "ADDRESS_COUNTRY"
    | "ADDRESS_ZIPCODE";
  Content: string | boolean;
}
export interface VoucherInfo {
  code: string;
  giftCardType: string;
  voucherData: VoucherDTO;
  // Codice del carnet da cui proviene questo voucher (se applicabile).
  // Serve a contare i voucher già usati per non superare l'UsageLimit del carnet.
  fromCarnetCode?: string;
}

// Riflesso minimo, per la sidebar, di una gift card applicata come anticipo
// pagamento in PaymentStep.tsx (Booking_AddAdvancedPayment). `maskedCardCode`
// arriva già mascherato da api.checkout.gift-card.tsx (maskCardCode lato
// server): il codice in chiaro non deve mai finire qui, dato che questo
// store è persistito in localStorage.
export interface AppliedGiftCard {
  id: string;
  maskedCardCode: string;
  amount: number;
  currency: string;
}

export interface BookingCart {
  BasketItems?: BookingCartItem[];
  GuestsData?: { PersonalData: GuestData[]; RefLine?: number }[];
  ReferentData?: ReferentData[];
  customReferentData?: CustomReferentData;
  customGuestsData?: CustomGuestData[];
  customTaxCodeOrVAT?: string;
  customTaxCode?: string;
  customVatId?: string;
  customCustomerType?: "private" | "company" | "pa";
  customCompanyName?: string;
  customLegalEmailAddress?: string;
  customInterchangeId?: string;
  DiscountCodes?: string[];
  DiscountItems?: Array<{
    Description?: string;
    Percentage?: number;
    Amount?: number;
  }>;
  TotalNetAmount?: {
    Price: number;
    Currency: string;
    VatIncluded: boolean;
  };
  vouchers?: VoucherInfo[];
}

export interface BookingCartItem {
  Type: string;
  ServiceType: string;
  LineNr: number;
  Service: string;
  Role: string;
  CheckinDate: string;
  StartTime: string;
  EndTime: string;
  Quantity: {
    ThirdType?: number;
    Total: number;
    Man: number;
    Woman: number;
  };
  GrossPrice: {
    Price: number;
    VatIncluded: boolean;
    Currency: string;
  };
  GrossAmount?: {
    Price: number;
    VatIncluded: boolean;
    Currency: string;
  };
  AdditionalField?: null | any;
  Included?: boolean; // True if this line is included in another one
  RefLine?: number;
  VoucherCode?: string;
  GiftCardType?: string;
}

export interface CartState {
  bookingCartOpened: boolean;
  mobileCartOpen: boolean;
  toggleBookingCart: () => void;
  setMobileCartOpen: (open: boolean) => void;
  setBookingCart: (bookingCart: BookingCart | null) => void;
  bookingCart: BookingCart | null;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  hasSignupCustomerId: boolean;
  setHasSignupCustomerId: (value: boolean) => void;
  clearCart: () => void;
  addVoucher: (voucher: VoucherInfo) => void;
  removeVoucher: (voucherCode: string) => void;
  clearVouchers: () => void;
  getMinExpirationDate: () => string | null;
  hasDifferentExpirationDates: () => boolean;
  // Gift card applicata come anticipo pagamento (PaymentStep.tsx), riflessa
  // qui solo perché la BookingCartSidebar legge il cart da questo store e
  // non ha altrimenti visibilità sulla sessione server-side della gift card.
  // È un pagamento anticipato, non uno sconto sulle righe del carrello: va
  // tenuta distinta da `vouchers`/`DiscountCodes`.
  appliedGiftCards: AppliedGiftCard[];
  giftCardRemaining: number | null;
  setAppliedGiftCards: (
    cards: AppliedGiftCard[],
    remaining: number | null,
  ) => void;
  clearAppliedGiftCards: () => void;
}

// Lo store del booking cart è creato due volte con chiavi `persist` distinte:
// uno per il flusso normale, uno per il flusso voucher. Così due tab aperti
// in parallelo (uno booking, uno voucher) non si sovrascrivono il cart in
// localStorage. La modalità da usare viene scelta dal Context Provider
// (vedi `useBookingCart` in `~/stores/bookingCartContext.tsx`).
const createBookingCartStore = (persistName: string) =>
  create<CartState>()(
  devtools(
    persist(
      (set, get) => ({
        bookingCartOpened: false,
        mobileCartOpen: false,
        toggleBookingCart: () =>
          set((state) => ({ bookingCartOpened: !state.bookingCartOpened })),
        setMobileCartOpen: (open: boolean) => {
          set({ mobileCartOpen: open });
        },
        setBookingCart: (bookingCart: BookingCart | null) => {
          set((state) => {
            // Preserva i voucher quando si imposta un nuovo carrello (anche se null)
            const existingVouchers = state.bookingCart?.vouchers;
            if (existingVouchers && existingVouchers.length > 0) {
              if (bookingCart) {
                return {
                  bookingCart: {
                    ...bookingCart,
                    vouchers: existingVouchers,
                  },
                };
              } else {
                // Anche se bookingCart è null, preserva i voucher
                return {
                  bookingCart: {
                    vouchers: existingVouchers,
                  } as BookingCart,
                };
              }
            }
            return { bookingCart };
          });
        },
        setLoading: (loading: boolean) => {
          set((state) => ({ loading }));
        },
        hasSignupCustomerId: false,
        setHasSignupCustomerId: (value: boolean) => {
          set({ hasSignupCustomerId: value });
        },
        clearCart: () => {
          fetch("/api/booking/delete-basket", {
            method: "DELETE",
          }).catch((error) => {
            console.error("Errore durante la cancellazione del basket:", error);
          });
          set((state) => {
            const existingVouchers = state.bookingCart?.vouchers;
            // Una nuova prenotazione azzera anche qualunque gift card
            // applicata a quella precedente: senza questo, una riga "gift
            // card applicata" della prenotazione abbandonata resterebbe
            // visibile in sidebar sulla prenotazione nuova.
            if (existingVouchers && existingVouchers.length > 0) {
              return {
                bookingCart: {
                  vouchers: existingVouchers,
                } as BookingCart,
                bookingCartOpened: false,
                mobileCartOpen: false,
                appliedGiftCards: [],
                giftCardRemaining: null,
              };
            }

            return {
              bookingCart: null,
              bookingCartOpened: false,
              mobileCartOpen: false,
              appliedGiftCards: [],
              giftCardRemaining: null,
            };
          });
        },
        addVoucher: (voucher: VoucherInfo) => {
          set((state) => {
            const currentVouchers = state.bookingCart?.vouchers || [];
            const existingIndex = currentVouchers.findIndex(
              (v) => v.code === voucher.code
            );

            let newVouchers: VoucherInfo[];
            if (existingIndex >= 0) {
              newVouchers = [...currentVouchers];
              newVouchers[existingIndex] = voucher;
            } else {
              newVouchers = [...currentVouchers, voucher];
            }
            return {
              bookingCart: {
                ...state.bookingCart,
                vouchers: newVouchers,
              } as BookingCart,
            };
          });
        },
        removeVoucher: (voucherCode: string) => {
          set((state) => {
            if (!state.bookingCart) return state;
            const currentVouchers = state.bookingCart.vouchers || [];
            const newVouchers = currentVouchers.filter(
              (v) => v.code !== voucherCode
            );
            return {
              bookingCart: {
                ...state.bookingCart,
                vouchers: newVouchers.length > 0 ? newVouchers : undefined,
              } as BookingCart,
            };
          });
        },
        clearVouchers: () => {
          set((state) => {
            if (!state.bookingCart) return state;
            return {
              bookingCart: {
                ...state.bookingCart,
                vouchers: undefined,
              } as BookingCart,
            };
          });
        },
        getMinExpirationDate: () => {
          const state = get();
          const vouchers = state.bookingCart?.vouchers || [];
          if (vouchers.length === 0) return null;

          const expirationDates = vouchers
            .map((v) => v.voucherData.ExpirationDate)
            .filter((date): date is string => !!date);

          if (expirationDates.length === 0) return null;

          return expirationDates.reduce((min, date) => {
            return date < min ? date : min;
          });
        },
        hasDifferentExpirationDates: () => {
          const state = get();
          const vouchers = state.bookingCart?.vouchers || [];
          if (vouchers.length <= 1) return false;

          const expirationDates = vouchers
            .map((v) => v.voucherData.ExpirationDate)
            .filter((date): date is string => !!date);

          if (expirationDates.length <= 1) return false;

          const uniqueDates = new Set(expirationDates);
          return uniqueDates.size > 1;
        },
        loading: true,
        bookingCart: null,
        appliedGiftCards: [],
        giftCardRemaining: null,
        setAppliedGiftCards: (cards, remaining) => {
          set({ appliedGiftCards: cards, giftCardRemaining: remaining });
        },
        clearAppliedGiftCards: () => {
          set({ appliedGiftCards: [], giftCardRemaining: null });
        },
      }),
      {
        name: persistName,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          bookingCart: state.bookingCart,
          bookingCartOpened: state.bookingCartOpened,
          vouchers: state.bookingCart?.vouchers,
          appliedGiftCards: state.appliedGiftCards,
          giftCardRemaining: state.giftCardRemaining,
        }),
        onRehydrateStorage: (state) => {
          return () => state.setLoading(false);
        },
      }
    )
  )
);

// Store del flusso booking normale.
export const useBookingCartStore = createBookingCartStore(
  "booking-cart-storage",
);

// Store del flusso voucher (riscatto + booking-voucher). Chiave `persist`
// distinta per non sovrascrivere il cart normale in localStorage.
export const useVoucherBookingCartStore = createBookingCartStore(
  "voucher-booking-cart-storage",
);

export type BookingCartStore = typeof useBookingCartStore;
